import { useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { GOOGLE_REVIEW_URL } from "@/constants/store";

export function Stars({ value, size = "w-4 h-4" }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${size} ${i <= Math.round(value) ? "fill-[#FFC220] text-[#C9971E]" : "fill-gray-200 text-gray-300"}`}
        />
      ))}
    </span>
  );
}

export default function ProductReviews({ product, stats, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const items = stats?.items || [];
  const visible = showAll ? items : items.slice(0, 4);

  const submit = async () => {
    if (!name.trim() || name.trim().length < 2) return toast.error("Please enter your name");
    if (!rating) return toast.error("Please pick a star rating");
    if (text.trim().length < 5) return toast.error("Please write a few words about your experience");
    setSaving(true);
    try {
      await api.post("/reviews", { product_id: product.id, name: name.trim(), rating, text: text.trim() });
      setSubmitted(true);
      setShowForm(false);
      setName(""); setRating(0); setText("");
      onRefresh?.();
      toast.success("Thank you — your review is live!");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || "Couldn't save your review — please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="reviews" className="mt-16 border-t-2 border-gray-200 pt-10" data-testid="product-reviews-section">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-display font-black text-2xl tracking-tight">Customer Reviews</h2>
          {stats?.count > 0 ? (
            <div className="mt-1.5 flex items-center gap-2" data-testid="reviews-average">
              <Stars value={stats.average} size="w-5 h-5" />
              <span className="font-bold">{stats.average}</span>
              <span className="text-sm text-gray-500">({stats.count} review{stats.count > 1 ? "s" : ""})</span>
            </div>
          ) : (
            <div className="mt-1.5 text-sm text-gray-500" data-testid="reviews-empty">
              No reviews yet — be the first to review this item.
            </div>
          )}
        </div>
        {!showForm && !submitted && (
          <button onClick={() => setShowForm(true)} className="btn-outline" data-testid="write-review-btn">
            ✍ Write a review
          </button>
        )}
      </div>

      {submitted && (
        <div className="mt-6 bg-[#FFC220] rounded-lg p-6" data-testid="review-thanks-banner">
          <div className="font-display font-black text-[#0A1C40] text-lg">Thank you! Your review is live. ⭐</div>
          <p className="text-sm text-[#0A1C40]/85 mt-1">
            Would you take 30 seconds to share it on Google too? It helps our small Grand Rapids business more than you know.
          </p>
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 mt-3 bg-[#0A1C40] text-[#FFC220] font-bold px-6 py-2.5 rounded-full text-sm hover:bg-black transition-colors"
            data-testid="review-google-cta"
          >
            ★ Post it on Google <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      )}

      {showForm && (
        <div className="mt-6 border-2 border-[#0A1C40] rounded-lg p-6 max-w-xl" data-testid="review-form">
          <div className="ipv-eyebrow mb-2">Your rating</div>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} type="button" onClick={() => setRating(i)} data-testid={`review-star-${i}`} aria-label={`${i} stars`}>
                <Star className={`w-8 h-8 transition-colors ${i <= rating ? "fill-[#FFC220] text-[#C9971E]" : "fill-gray-100 text-gray-300 hover:text-[#C9971E]"}`} />
              </button>
            ))}
          </div>
          <input
            className="input-base mt-4"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="review-name-input"
          />
          <textarea
            className="input-base mt-3"
            rows={3}
            placeholder="How was the deal? The pickup or delivery? Tell other shoppers…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="review-text-input"
          />
          <div className="mt-4 flex gap-3">
            <button onClick={submit} disabled={saving} className="btn-gold disabled:opacity-50" data-testid="review-submit-btn">
              {saving ? "Posting…" : "Post review"}
            </button>
            <button onClick={() => setShowForm(false)} className="btn-outline" data-testid="review-cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      )}

      {visible.length > 0 && (
        <div className="mt-8 space-y-5">
          {visible.map((r) => (
            <div key={r.id} className="border border-gray-200 rounded-lg p-5" data-testid={`review-item-${r.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <Stars value={r.rating} />
                <span className="font-bold text-sm">{r.name}</span>
                <span className="text-xs text-gray-400">{(r.created_at || "").slice(0, 10)}</span>
              </div>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed whitespace-pre-line">{r.text}</p>
            </div>
          ))}
          {items.length > 4 && !showAll && (
            <button onClick={() => setShowAll(true)} className="text-sm font-bold text-[#0A1C40] underline" data-testid="reviews-show-all">
              Show all {items.length} reviews
            </button>
          )}
        </div>
      )}

      <a
        href={GOOGLE_REVIEW_URL}
        target="_blank"
        rel="noreferrer"
        className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#0A1C40] hover:text-[#C9971E] transition-colors"
        data-testid="reviews-google-link"
      >
        ★ Shopped with us before? Review us on Google <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </section>
  );
}
